import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "./ManageSignaturesPage.css"; // Crear este archivo CSS

function ManageSignaturesPage() {
  const [signatures, setSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(""); // Mensajes de éxito
  const [showErrorPopup, setShowErrorPopup] = useState(false); // Estado para pop-up de error
  const [errorPopupMessage, setErrorPopupMessage] = useState(""); // Mensaje de pop-up de error

  // Estados para Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [signaturesPerPage] = useState(10); // Mostrar 10 firmas por página

  // Estados para Búsqueda y Filtro
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("name"); // Buscar por nombre de firmante

  const { token } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // Función para obtener todas las firmas del backend
  const fetchSignatures = async () => {
    setIsLoading(true);
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    try {
      const response = await axios.get(`${API_BASE_URL}/signatures`, {
        // Nueva ruta /signatures
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSignatures(response.data);
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al cargar las firmas."
      );
      setShowErrorPopup(true);
      console.error("Error fetching signatures:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar las firmas al montar el componente o cuando el token cambia
  useEffect(() => {
    if (token) {
      fetchSignatures();
    } else {
      setErrorPopupMessage("No autenticado. Por favor, inicie sesión.");
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  }, [token, API_BASE_URL]);

  // Lógica de Filtrado en Tiempo Real
  const filteredSignatures = useMemo(() => {
    if (!searchTerm) {
      return signatures;
    }
    return signatures.filter((signature) => {
      const value = String(signature[searchColumn]).toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    });
  }, [signatures, searchTerm, searchColumn]);

  // Lógica de Paginación
  const indexOfLastSignature = currentPage * signaturesPerPage;
  const indexOfFirstSignature = indexOfLastSignature - signaturesPerPage;
  const currentSignatures = filteredSignatures.slice(
    indexOfFirstSignature,
    indexOfLastSignature
  );
  const totalPages = Math.ceil(filteredSignatures.length / signaturesPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Función para editar una firma
  const handleEditSignature = (signatureId) => {
    navigate(`/admin/signatures/edit/${signatureId}`);
  };

  // Función para eliminar una firma
  const handleDeleteSignature = async (signatureId) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar esta firma? Esta acción no se puede deshacer y puede afectar eventos asociados."
      )
    ) {
      return;
    }
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    setIsLoading(true);

    try {
      await axios.delete(`${API_BASE_URL}/signatures/${signatureId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Firma eliminada exitosamente.");
      fetchSignatures(); // Recargar la lista
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al eliminar la firma."
      );
      setShowErrorPopup(true);
      console.error("Error deleting signature:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar el pop-up de error
  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorPopupMessage("");
  };

  if (isLoading) {
    return (
      <div className="manage-signatures-container">
        <p>Cargando firmas...</p>
      </div>
    );
  }

  // Devuelve un array de páginas a mostrar en la paginación (máximo 7)
  const getVisiblePages = () => {
    const maxVisible = 7;
    let start = Math.max(currentPage - Math.floor(maxVisible / 2), 1);
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxVisible + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="manage-signatures-container">
      <div className="manage-signatures-card">
        <h2 className="manage-signatures-title">Gestión de Firmas</h2>
        <p className="manage-signatures-description">
          Administra los nombres y roles de los firmantes de certificados, así
          como sus firmas digitales.
        </p>

        <div className="top-controls">
          <Link
            to="/admin/signatures/create"
            className="create-signature-button"
          >
            <span className="material-icons">add</span> Crear Nueva Firma
          </Link>
          <div className="search-filter-container">
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
              className="search-column-select"
            >
              <option value="name">Nombre del Firmante</option>
              <option value="role">Rol del Firmante</option>
            </select>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
        </div>

        {message && (
          <p className="manage-signatures-message success">{message}</p>
        )}

        {filteredSignatures.length === 0 && searchTerm !== "" ? (
          <p className="no-results-message">
            No se encontraron firmas que coincidan con la búsqueda.
          </p>
        ) : filteredSignatures.length === 0 ? (
          <p className="no-results-message">
            No hay firmas registradas.{" "}
            <Link to="/admin/signatures/create">Crea una ahora</Link>.
          </p>
        ) : (
          <table className="signatures-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre del Firmante</th>
                <th>Rol</th>
                <th>Firma Digital</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentSignatures.map((signature) => (
                <tr key={signature.id}>
                  <td>{signature.id}</td>
                  <td>{signature.name}</td>
                  <td>{signature.role}</td>
                  <td>
                    {signature.digital_signature_url ? (
                      <img
                        src={`${API_BASE_URL}/uploads/${signature.digital_signature_url}`}
                        alt={`Firma de ${signature.name}`}
                        className="signature-thumbnail"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://placehold.co/80x30/cccccc/333333?text=Sin+Firma";
                        }}
                      />
                    ) : (
                      <span>Sin firma</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons-container">
                      <button
                        onClick={() => handleEditSignature(signature.id)}
                        className="action-button edit-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">edit</span>
                        <span className="button-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDeleteSignature(signature.id)}
                        className="action-button delete-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">delete</span>
                        <span className="button-text">Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Controles de Paginación */}
        {filteredSignatures.length > signaturesPerPage && (
          <div className="pagination-controls">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Anterior
            </button>

            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`pagination-button ${
                  currentPage === page ? "active" : ""
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Pop-up de Error */}
      {showErrorPopup && (
        <div className="popup-overlay">
          <div className="popup-content error-popup">
            <h3>¡Error!</h3>
            <p>{errorPopupMessage}</p>
            <button
              onClick={handleCloseErrorPopup}
              className="popup-button error-button"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageSignaturesPage;
